/**
 * Scene-level actions (create / switch / rename / duplicate / delete).
 *
 * Scene swaps follow the collab-join pattern: `resetScene()` (clears scene,
 * store and undo history — undo cannot cross scenes) followed by
 * `updateScene({ captureUpdate: NEVER })`.
 */

import { CaptureUpdateAction } from "@excalidraw/excalidraw";
import { clearAppStateForLocalStorage } from "@excalidraw/excalidraw/appState";
import { loadFromBlob } from "@excalidraw/excalidraw/data/blob";
import { fileOpen } from "@excalidraw/excalidraw/data/filesystem";
import {
  restoreAppState,
  restoreElements,
} from "@excalidraw/excalidraw/data/restore";
import { isInitializedImageElement } from "@excalidraw/element";

import type { ExcalidrawElement, FileId } from "@excalidraw/element/types";
import type { RestoredDataState } from "@excalidraw/excalidraw/data/restore";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { appJotaiStore } from "../app-jotai";
import { isCollaboratingAtom } from "../collab/Collab";
import { updateStaleImageStatuses } from "../data/FileManager";
import { LocalData } from "../data/LocalData";

import { materializeScene } from "./materialize";
import { reorderKeys, sortByOrder } from "./ordering";
import { getScenesIndex, setScenesIndex } from "./state";
import { scenesStorage, newSceneId } from "./storage";

import type { CollectionId, SceneId, SceneMeta, ScenesIndex } from "./storage";

const isCollaborating = () => !!appJotaiStore.get(isCollaboratingAtom);

const nextUntitledName = (index: ScenesIndex) => {
  const names = new Set(index.scenes.map((scene) => scene.name));
  if (!names.has("Untitled")) {
    return "Untitled";
  }
  let counter = 2;
  while (names.has(`Untitled ${counter}`)) {
    counter++;
  }
  return `Untitled ${counter}`;
};

/** loads the given elements' image files from local IDB into the scene */
export const loadSceneImages = (
  excalidrawAPI: ExcalidrawImperativeAPI,
  elements: readonly ExcalidrawElement[],
) => {
  const fileIds = elements.reduce((acc, element) => {
    if (isInitializedImageElement(element)) {
      acc.push(element.fileId);
    }
    return acc;
  }, [] as FileId[]);

  if (!fileIds.length) {
    return;
  }

  LocalData.fileStorage
    .getFiles(fileIds)
    .then(({ loadedFiles, erroredFiles }) => {
      if (loadedFiles.length) {
        excalidrawAPI.addFiles(loadedFiles);
      }
      updateStaleImageStatuses({
        excalidrawAPI,
        erroredFiles,
        elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
      });
    });
};

/**
 * Replaces the scene with the given scene's persisted data (load-only —
 * does not touch the index). Also used by cross-tab sync when another tab
 * switched the active scene.
 */
export const applyStoredScene = async (
  id: SceneId,
  excalidrawAPI: ExcalidrawImperativeAPI,
): Promise<boolean> => {
  const index = getScenesIndex();
  const meta = index.scenes.find((scene) => scene.id === id);

  // A scene whose metadata exists but whose payload does not (only a cloud
  // pull produces this) must NOT be rendered until the payload is here —
  // below, missing keys are read as an empty scene, which is correct for a
  // never-yet-saved scene and catastrophic for an undownloaded one: the
  // autosave would persist the emptiness and it would replicate over the
  // real data. Fetch it first, and if that can't happen (signed out, or the
  // download failed) bail without touching the editor; the caller keeps
  // showing the previous scene.
  if (meta?.materialized === false && !(await materializeScene(id))) {
    console.warn(`scene ${id} is not materialized locally — not applying`);
    return false;
  }

  // missing scene keys ≡ empty scene (e.g. a new scene before its first save)
  const data = await scenesStorage.loadScene(id);

  const elements = restoreElements(data?.elements ?? [], null, {
    repairBindings: true,
  });
  const appState = {
    ...restoreAppState(data?.appState ?? null, null),
    // the index is the source of truth for the scene name
    name: meta?.name ?? "Untitled",
    // don't let the target scene's persisted state slam the sidebar shut
    // while the user is browsing scenes
    openSidebar: excalidrawAPI.getAppState().openSidebar,
    // theme is a user-wide setting, not scene state — keep the current one
    // (also neutralizes the theme scenes persisted before it was excluded
    // from browser storage)
    theme: excalidrawAPI.getAppState().theme,
    isLoading: false,
  };

  excalidrawAPI.resetScene();
  excalidrawAPI.updateScene({
    elements,
    appState,
    captureUpdate: CaptureUpdateAction.NEVER,
  });

  loadSceneImages(excalidrawAPI, elements);
  return true;
};

export const switchToScene = async (
  id: SceneId,
  excalidrawAPI: ExcalidrawImperativeAPI,
) => {
  const index = getScenesIndex();
  if (
    id === index.activeSceneId ||
    !index.scenes.some((scene) => scene.id === id) ||
    isCollaborating()
  ) {
    return;
  }

  LocalData.flushSave();
  LocalData.pauseSave("switchingScene");
  try {
    // updater form — the flush above may have bumped the outgoing scene's meta
    setScenesIndex((prev) => ({ ...prev, activeSceneId: id }));
    await applyStoredScene(id, excalidrawAPI);
  } finally {
    LocalData.resumeSave("switchingScene");
  }
};

/**
 * Adds a new empty scene to the index without switching to it — a pure
 * index operation, so no collab gating and no save pausing. Returns the
 * new meta so the caller can start an inline rename.
 */
export const createScene = (
  collectionId: CollectionId | null = null,
): SceneMeta => {
  const index = getScenesIndex();
  const now = Date.now();
  const meta: SceneMeta = {
    id: newSceneId(),
    name: nextUntitledName(index),
    createdAt: now,
    updatedAt: now,
    collectionId,
  };
  // no scene keys are written until the scene is opened and first edited —
  // missing keys load as an empty scene
  setScenesIndex({ ...index, scenes: [...index.scenes, meta] });
  return meta;
};

/**
 * Adds an already-loaded scene (as returned by `loadFromBlob`) to the
 * workspace as a new scene and switches to it. Returns the new scene's id,
 * or null while collaborating (the caller decides the fallback).
 */
export const importSceneFromData = async (
  data: Pick<RestoredDataState, "elements" | "appState" | "files">,
  name: string | null,
  excalidrawAPI: ExcalidrawImperativeAPI,
  collectionId: CollectionId | null = null,
): Promise<SceneId | null> => {
  if (isCollaborating()) {
    return null;
  }

  const id = newSceneId();
  // embedded images go into the shared IDB files store, same as images
  // added while drawing
  if (data.files && Object.keys(data.files).length) {
    await LocalData.fileStorage.saveFiles({
      elements: data.elements,
      files: data.files,
    });
  }
  await scenesStorage.saveScene(id, {
    elements: data.elements,
    appState: clearAppStateForLocalStorage(data.appState),
  });

  const sceneName = name?.trim() || "Untitled";

  LocalData.flushSave();
  LocalData.pauseSave("switchingScene");
  try {
    // updater form — the flush above may have bumped the outgoing scene's meta
    const now = Date.now();
    setScenesIndex((prev) => ({
      ...prev,
      activeSceneId: id,
      scenes: [
        ...prev.scenes,
        { id, name: sceneName, createdAt: now, updatedAt: now, collectionId },
      ],
    }));
    await applyStoredScene(id, excalidrawAPI);
  } finally {
    LocalData.resumeSave("switchingScene");
  }
  return id;
};

/**
 * Imports an .excalidraw file as a new scene and switches to it.
 * Returns the new scene's id, or null if the user cancelled the file picker.
 */
export const importScene = async (
  excalidrawAPI: ExcalidrawImperativeAPI,
  collectionId: CollectionId | null = null,
): Promise<SceneId | null> => {
  if (isCollaborating()) {
    return null;
  }

  let file: File;
  try {
    file = await fileOpen({
      description: "Excalidraw files",
      extensions: ["excalidraw"],
    });
  } catch (error: any) {
    if (error?.name === "AbortError") {
      return null;
    }
    throw error;
  }

  const data = await loadFromBlob(file, null, null);

  const name =
    data.appState.name?.trim() || file.name.replace(/\.excalidraw$/i, "");

  return importSceneFromData(data, name, excalidrawAPI, collectionId);
};

export const renameScene = (
  id: SceneId,
  name: string,
  excalidrawAPI: ExcalidrawImperativeAPI,
) => {
  const trimmedName = name.trim();
  const index = getScenesIndex();
  if (!trimmedName || !index.scenes.some((scene) => scene.id === id)) {
    return;
  }

  setScenesIndex({
    ...index,
    scenes: index.scenes.map((scene) =>
      scene.id === id ? { ...scene, name: trimmedName } : scene,
    ),
  });

  if (id === index.activeSceneId) {
    excalidrawAPI.updateScene({
      appState: { name: trimmedName },
      captureUpdate: CaptureUpdateAction.NEVER,
    });
  }
};

export const duplicateScene = async (id: SceneId) => {
  const index = getScenesIndex();
  const sourceMeta = index.scenes.find((scene) => scene.id === id);
  if (!sourceMeta) {
    return;
  }

  if (id === index.activeSceneId) {
    // make sure the copy includes the latest scene
    LocalData.flushSave();
  }

  const data = await scenesStorage.loadScene(id);
  const newId = newSceneId();
  if (data) {
    try {
      // images need zero work — content-addressed fileIds in the shared IDB
      // store are shared between scenes
      await scenesStorage.saveScene(newId, {
        elements: data.elements,
        appState: data.appState ?? {},
      });
    } catch (error: any) {
      console.error(error);
      return;
    }
  }

  const now = Date.now();
  // updater form — the load/save awaits above give other mutations a window
  setScenesIndex((prev) => ({
    ...prev,
    scenes: [
      ...prev.scenes,
      {
        id: newId,
        name: `${sourceMeta.name} (copy)`,
        createdAt: now,
        updatedAt: now,
        collectionId: sourceMeta.collectionId,
        contentVersion: sourceMeta.contentVersion,
      },
    ],
  }));
};

/**
 * Moves a scene next to another one — a pure index operation (no collab
 * gating, no save pausing). The position is stamped as a sort key rather
 * than spliced into the array: cloud sync merges scene by scene, so an array
 * position would never reach another device. See `ordering.ts`.
 */
export const reorderScene = (
  id: SceneId,
  targetId: SceneId,
  position: "before" | "after",
) => {
  const index = getScenesIndex();
  const keys = reorderKeys(index.scenes, id, targetId, position);
  if (!keys) {
    return;
  }
  const now = Date.now();
  setScenesIndex({
    ...index,
    scenes: sortByOrder(
      index.scenes.map((scene) => {
        const order = keys.get(scene.id);
        // `updatedAt` must not move: it is a content clock (it bumps on
        // pan/zoom), and bumping it here would let a reorder win the
        // last-write-wins race against a real edit made on another device
        return order === undefined
          ? scene
          : { ...scene, order, orderedAt: now };
      }),
    ),
  });
};

export const deleteScene = async (
  id: SceneId,
  excalidrawAPI: ExcalidrawImperativeAPI,
) => {
  const index = getScenesIndex();
  const scenePosition = index.scenes.findIndex((scene) => scene.id === id);
  if (scenePosition === -1) {
    return;
  }

  if (id !== index.activeSceneId) {
    setScenesIndex({
      ...index,
      scenes: index.scenes.filter((scene) => scene.id !== id),
    });
    await scenesStorage.deleteScene(id);
    // orphaned images age out via the 24h rule at next startup
    return;
  }

  if (isCollaborating()) {
    return;
  }

  // drop pending writes of the scene being deleted — don't flush them
  LocalData.cancelSave();

  let scenes = index.scenes.filter((scene) => scene.id !== id);
  let fallbackId: SceneId;
  if (scenes.length) {
    fallbackId = (
      index.scenes[scenePosition + 1] ?? index.scenes[scenePosition - 1]
    ).id;
  } else {
    const now = Date.now();
    const fresh = {
      id: newSceneId(),
      name: "Untitled",
      createdAt: now,
      updatedAt: now,
    };
    scenes = [fresh];
    fallbackId = fresh.id;
  }

  LocalData.pauseSave("switchingScene");
  try {
    setScenesIndex({ ...index, activeSceneId: fallbackId, scenes });
    await scenesStorage.deleteScene(id);
    await applyStoredScene(fallbackId, excalidrawAPI);
  } finally {
    LocalData.resumeSave("switchingScene");
  }
};
